import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { COUNTRY_OPTIONS, LANGUAGE_OPTIONS } from '../../constants/profileOptions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch (_err) {
    return value;
  }
};

const SettingsProfile = () => {
  const { userProfile, updateUserProfile, refreshUserProfile, profileLoading } = useAuth();
  const toast = useToast();
  const { t, i18n } = useTranslation('settings');
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    displayName: '',
    avatarUrl: '',
    country: 'FR',
    language: 'fr',
    marketingConsent: false,
  });

  useEffect(() => {
    if (!userProfile) return;
    setFormData({
      displayName: userProfile.profile?.display_name || '',
      avatarUrl: userProfile.profile?.avatar_url || '',
      country: userProfile.legal?.country || 'FR',
      language: userProfile.legal?.language || 'fr',
      marketingConsent: Boolean(userProfile.profile?.marketing_consent),
    });
  }, [userProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateUserProfile({
        profile: {
          display_name: formData.displayName,
          avatar_url: formData.avatarUrl || null,
          marketing_consent: formData.marketingConsent,
        },
        legal: {
          country: formData.country,
          language: formData.language,
        },
      });
      toast.success(t('profile.toastSaved'));
      await refreshUserProfile();
    } catch (error) {
      toast.error(error?.message || t('profile.toastError'));
    } finally {
      setSaving(false);
    }
  };

  if (profileLoading && !userProfile) {
    return <div className="p-8 text-center text-slate-500">{t('status.loadingProfile')}</div>;
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-gray-800 pb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{t('profile.title')}</h2>
          <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">
            {t('profile.subtitle')}
          </p>
        </div>
        <div className="hidden sm:block">
            <div className="h-12 w-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                <FontAwesomeIcon icon="id-card" className="text-xl" />
            </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section Avatar & Nom */}
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                    <label htmlFor="displayName" className="text-sm font-medium text-slate-700 dark:text-gray-300">
                        {t('profile.displayName')}
                    </label>
                    <input
                        id="displayName"
                        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white transition-all"
                        value={formData.displayName}
                        onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                        required
                        placeholder={t('profile.placeholderDisplayName')}
                    />
                </div>
                <div className="space-y-2">
                    <label htmlFor="avatarUrl" className="text-sm font-medium text-slate-700 dark:text-gray-300">
                        {t('profile.avatar')}
                    </label>
                    <input
                        id="avatarUrl"
                        className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white transition-all"
                        value={formData.avatarUrl}
                        onChange={(e) => setFormData({ ...formData, avatarUrl: e.target.value })}
                        placeholder="https://..."
                    />
                </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                    <label htmlFor="country" className="text-sm font-medium text-slate-700 dark:text-gray-300">
                        {t('profile.country')}
                    </label>
                    <div className="relative">
                        <select
                            id="country"
                            value={formData.country}
                            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                            className="w-full appearance-none rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white transition-all"
                        >
                            {COUNTRY_OPTIONS.map((option) => (
                            <option key={option.code} value={option.code}>
                                {option.label}
                            </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                            <FontAwesomeIcon icon="chevron-down" className="text-xs" />
                        </div>
                    </div>
                </div>
                <div className="space-y-2">
                    <label htmlFor="language" className="text-sm font-medium text-slate-700 dark:text-gray-300">
                        {t('profile.language')}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        {LANGUAGE_OPTIONS.map((option) => {
                            const isSelected = formData.language === option.code;
                            return (
                                <button
                                    key={option.code}
                                    type="button"
                                    onClick={() => {
                                        setFormData({ ...formData, language: option.code });
                                        i18n.changeLanguage(option.code);
                                        localStorage.setItem('muzo_lang', option.code);
                                    }}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all duration-200 ${
                                        isSelected
                                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 shadow-md ring-2 ring-indigo-500/20'
                                            : 'border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-sm'
                                    }`}
                                >
                                    <span className="text-2xl">{option.flag}</span>
                                    <div className="flex-1 text-left">
                                        <p className={`text-sm font-medium ${
                                            isSelected 
                                                ? 'text-indigo-700 dark:text-indigo-300' 
                                                : 'text-slate-700 dark:text-gray-300'
                                        }`}>
                                            {option.label}
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-gray-400">
                                            {option.native}
                                        </p>
                                    </div>
                                    {isSelected && (
                                        <FontAwesomeIcon 
                                            icon="check-circle" 
                                            className="text-indigo-500 dark:text-indigo-400" 
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="pt-4">
                <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-900/50 cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors">
                    <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={formData.marketingConsent}
                        onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                    />
                    <div className="text-sm">
                        <span className="font-medium text-slate-900 dark:text-white block">{t('profile.newsletterTitle')}</span>
                        <span className="text-slate-500 dark:text-gray-400">{t('profile.newsletterDesc')}</span>
                    </div>
                </label>
            </div>
          </div>

          {/* Sidebar Info */}
          <div className="space-y-6">
            {/* Avatar Preview */}
            <div className="flex flex-col items-center justify-center p-6 rounded-2xl border border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-900/30">
                <div className="relative h-24 w-24 rounded-full overflow-hidden bg-slate-200 dark:bg-gray-800 ring-4 ring-white dark:ring-gray-900 shadow-lg mb-4">
                    {formData.avatarUrl ? (
                        <img
                            src={formData.avatarUrl}
                            alt="Avatar"
                            className="h-full w-full object-cover"
                            onError={() => setFormData({ ...formData, avatarUrl: '' })}
                        />
                    ) : (
                        <div className="h-full w-full flex items-center justify-center text-slate-400">
                            <FontAwesomeIcon icon="user" className="text-3xl" />
                        </div>
                    )}
                </div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">{formData.displayName || t('profile.user')}</p>
                <p className="text-xs text-slate-500 dark:text-gray-400">{userProfile?.email}</p>
            </div>

            {/* Account Details */}
            <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-5 space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-gray-500">{t('profile.accountDetails')}</h3>
                
                <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-gray-400">{t('profile.signup')}</span>
                    <span className="font-medium text-slate-900 dark:text-white">{formatDate(userProfile?.profile?.signup_date)}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-gray-400">{t('profile.stripeId')}</span>
                    <span className="font-mono text-xs bg-slate-100 dark:bg-gray-800 px-2 py-1 rounded text-slate-600 dark:text-gray-300">
                        {userProfile?.legal?.stripe_customer_id || 'N/A'}
                    </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-gray-400">{t('profile.terms')}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        userProfile?.legal?.terms?.accepted 
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                    }`}>
                        {userProfile?.legal?.terms?.accepted ? t('profile.termsAccepted') : t('profile.termsPending')}
                    </span>
                </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t border-slate-200 dark:border-gray-800">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-8 py-3 font-semibold text-white transition-all hover:bg-indigo-500 hover:shadow-lg hover:shadow-indigo-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
                <>
                    <FontAwesomeIcon icon="circle-notch" spin />
                    <span>{t('profile.saving')}</span>
                </>
            ) : (
                <>
                    <FontAwesomeIcon icon="save" />
                    <span>{t('profile.save')}</span>
                </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettingsProfile;
